// Service requests — a client books a service through the public portal (a separate, public worker on
// its own subdomain); the row lands in a shared `service_requests` table that ROLODEX reads so assembly
// staff can approve or decline it. The portal and ROLODEX both use the DDL + schema below, so the two
// deployments stay in lock-step. For now this covers service only.
import { z } from 'zod';
// One shared table definition so the portal worker and the ROLODEX worker create an identical table
// (both self-heal it on first use; a hand-written migration mirrors it for prod).
export const SERVICE_REQUESTS_DDL = `CREATE TABLE IF NOT EXISTS service_requests (
  id TEXT PRIMARY KEY,
  ref TEXT UNIQUE NOT NULL,
  vessel_name TEXT NOT NULL,
  client_name TEXT NOT NULL,
  email TEXT NOT NULL,
  phone TEXT NOT NULL DEFAULT '',
  hin TEXT NOT NULL DEFAULT '',
  details TEXT NOT NULL DEFAULT '',
  preferred_date TEXT NOT NULL DEFAULT '',
  status TEXT NOT NULL DEFAULT 'new',
  decided_by TEXT NOT NULL DEFAULT '',
  decided_at TEXT NOT NULL DEFAULT '',
  admin_note TEXT NOT NULL DEFAULT '',
  proposed_date TEXT NOT NULL DEFAULT '',
  client_emailed_at TEXT NOT NULL DEFAULT '',
  notified_at TEXT NOT NULL DEFAULT '',
  created_at TEXT NOT NULL DEFAULT (datetime('now')),
  updated_at TEXT NOT NULL DEFAULT (datetime('now'))
)`;
// Columns added after the table first shipped — applied by the ROLODEX side's ensureServiceRequestsTable
// as ALTER ... ADD COLUMN (a fresh table already has them from the DDL above). `notified_at` is internal
// (guards the "new request" staff email); the other two are shown on the Service Requests page.
export const SERVICE_REQUESTS_ADD_COLUMNS = [
    "ALTER TABLE service_requests ADD COLUMN proposed_date TEXT NOT NULL DEFAULT ''",
    "ALTER TABLE service_requests ADD COLUMN client_emailed_at TEXT NOT NULL DEFAULT ''",
    "ALTER TABLE service_requests ADD COLUMN notified_at TEXT NOT NULL DEFAULT ''",
];
export const SERVICE_REQUESTS_INDEX = `CREATE INDEX IF NOT EXISTS idx_service_requests_status ON service_requests(status, created_at)`;
// The public booking form (portal → shared DB). Small and strict; free text is length-bounded, and a
// hidden `website` honeypot must stay empty (bots fill every field).
export const ServiceRequestBookSchema = z.object({
    vesselName: z.string().trim().min(1, 'Vessel name is required').max(120),
    clientName: z.string().trim().min(1, 'Your name is required').max(120),
    email: z.string().trim().email('A valid email is required').max(160),
    phone: z.string().trim().min(1, 'A phone number is required').max(40),
    hin: z.string().trim().min(1, 'The vessel HIN is required').max(40),
    details: z.string().trim().max(2000).optional().default(''),
    preferredDate: z.string().trim().max(20).optional().default(''),
    // GDPR: the client must acknowledge the privacy notice before we process their data. Enforced
    // server-side too, so a request can't be saved without it.
    consent: z.boolean().refine((v) => v === true, { message: 'Please confirm you have read the Privacy Notice.' }),
    website: z.string().max(0).optional(), // honeypot — real users never see this field
});
// The approve / decline decision taken by the service team inside ROLODEX. Approving with a
// `proposedDate` is the "approve and shift the date" case. When `sendEmail` is true the worker emails
// the client (at the request's stored address — never a caller-supplied one) the given subject/body,
// which the staff member can edit before sending; blank subject/body fall back to a built-in default.
export const ServiceRequestDecideSchema = z.object({
    id: z.string().min(1).max(64),
    status: z.enum(['approved', 'declined']),
    note: z.string().trim().max(2000).optional().default(''),
    proposedDate: z.string().trim().max(20).optional().default(''),
    sendEmail: z.boolean().optional().default(false),
    emailSubject: z.string().trim().max(200).optional().default(''),
    emailBody: z.string().trim().max(4000).optional().default(''),
});
/** a short, unambiguous client-facing reference (no easily-confused characters). Pass a random source
 *  so this stays pure/testable; the worker feeds it crypto-random bytes. */
export function serviceRequestRef(rand) {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no I/O/0/1
    let out = '';
    for (let i = 0; i < 6; i++)
        out += alphabet[Math.floor(rand() * alphabet.length)] ?? 'X';
    return `SR-${out}`;
}
//# sourceMappingURL=serviceRequest.js.map